import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PiCubeBold, PiMagnifyingGlassBold } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import { Button } from '@v2/design-system';
import api from '@v2/lib/api';

export type PiCheckSkuRow = {
    product_code: string;
    internal_code: string;
    supplier_code: string | null;
    product_id: number | null;
    product_name: string | null;
    catalog_cost_usd: number | null;
    image_url: string | null;
    po_source_codes: string[];
    pi_source_codes: string[];
    po_line_count?: number;
    pi_line_count?: number;
    sku_linked: boolean;
    po: { unit_price: number; name: string | null } | null;
    pi: { unit_price: number; name: string | null } | null;
};

type ProductOption = {
    id: number;
    code: string | null;
    supplier_code: string | null;
    name: string;
    image_url: string | null;
    cost_usd: number | null;
};

const fmtUSD = (n: number | null | undefined) =>
    n == null ? '—' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function suggestInternal(row: PiCheckSkuRow): string {
    return (
        row.internal_code?.trim() ||
        row.po_source_codes[0]?.trim() ||
        row.pi_source_codes[0]?.trim() ||
        ''
    );
}

function suggestSupplier(row: PiCheckSkuRow): string {
    return (
        row.supplier_code?.trim() ||
        row.pi_source_codes[0]?.trim() ||
        row.po_source_codes[0]?.trim() ||
        ''
    );
}

type Props = {
    row: PiCheckSkuRow | null;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
};

export default function PiCheckSkuModal({ row, open, onClose, onSaved }: Props) {
    const [productId, setProductId] = useState<number | null>(null);
    const [internalCode, setInternalCode] = useState('');
    const [supplierCode, setSupplierCode] = useState('');
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);

    const searchTerm = useMemo(() => {
        if (search.trim()) return search.trim();
        if (!row) return '';
        return (
            row.po_source_codes[0] ||
            row.pi_source_codes[0] ||
            row.internal_code ||
            ''
        );
    }, [row, search]);

    useEffect(() => {
        if (!row || !open) return;
        setProductId(row.product_id);
        setInternalCode(suggestInternal(row));
        setSupplierCode(suggestSupplier(row));
        setSearch('');
        setError(null);
    }, [row, open]);

    const productQuery = useQuery({
        queryKey: ['products', productId, 'pi-check-modal'],
        enabled: open && productId != null,
        queryFn: async (): Promise<ProductOption> => {
            const res = await api.get(`/products/${productId}`);
            const p = res.data.data ?? res.data;
            return {
                id: p.id,
                code: p.code,
                supplier_code: p.supplier_code,
                name: p.name,
                image_url: p.image_url,
                cost_usd: p.cost_usd,
            };
        },
    });

    useEffect(() => {
        if (productQuery.data) {
            setInternalCode(productQuery.data.code ?? '');
            setSupplierCode(productQuery.data.supplier_code ?? '');
        }
    }, [productQuery.data]);

    const searchQuery = useQuery({
        queryKey: ['products', 'pi-check-search', searchTerm],
        enabled: open && !productId && searchTerm.length >= 2,
        queryFn: async () => {
            const { data } = await api.get<{ data: ProductOption[] }>('/products', {
                params: { search: searchTerm, per_page: 8 },
            });
            return data.data ?? [];
        },
    });

    const saveMut = useMutation({
        mutationFn: async () => {
            if (!productId) {
                throw new Error('Selecione um produto do catálogo.');
            }
            await api.put(`/products/${productId}`, {
                code: internalCode.trim() || null,
                supplier_code: supplierCode.trim() || null,
            });
        },
        onSuccess: () => {
            setError(null);
            onSaved();
            onClose();
        },
        onError: (e: unknown) => {
            const err = e as { response?: { data?: { message?: string } }; message?: string };
            setError(err?.response?.data?.message ?? err?.message ?? 'Erro ao salvar.');
        },
    });

    if (!row) return null;

    const displayProduct = productQuery.data;
    const imageUrl = displayProduct?.image_url ?? row.image_url;
    const name = displayProduct?.name ?? row.product_name ?? row.po?.name ?? row.pi?.name ?? '—';
    const catalogPrice = displayProduct?.cost_usd ?? row.catalog_cost_usd;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title="Vincular SKUs do produto"
            description="Ajuste o SKU interno (PO) e o SKU fornecedor (PI) para a conferência bater corretamente."
            size="lg"
            footer={
                <>
                    <Button variant="outline" onClick={onClose} disabled={saveMut.isPending}>
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => saveMut.mutate()}
                        loading={saveMut.isPending}
                        disabled={!productId}
                    >
                        Salvar SKUs
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                {error ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}

                <div className="flex gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt=""
                            className="size-20 flex-shrink-0 rounded-lg object-cover ring-1 ring-gray-200"
                        />
                    ) : (
                        <div className="flex size-20 flex-shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-gray-200">
                            <PiCubeBold className="size-8 text-gray-300" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="text-lg font-semibold text-gray-900">{name}</div>
                        <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    Preço catálogo
                                </div>
                                <div className="font-medium text-gray-800">{fmtUSD(catalogPrice)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                    Preço PO
                                </div>
                                <div className="font-medium text-gray-800">
                                    {fmtUSD(row.po?.unit_price)}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                                    Preço PI
                                </div>
                                <div className="font-medium text-gray-800">
                                    {fmtUSD(row.pi?.unit_price)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {!productId ? (
                    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <p className="text-sm text-amber-900">
                            Produto não encontrado no catálogo. Busque pelo nome ou código da PO/PI:
                        </p>
                        <div className="relative">
                            <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchTerm || 'Código ou nome…'}
                                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
                            />
                        </div>
                        {searchQuery.isFetching ? (
                            <p className="text-xs text-gray-500">Buscando…</p>
                        ) : null}
                        {(searchQuery.data?.length ?? 0) > 0 ? (
                            <ul className="max-h-40 space-y-1 overflow-y-auto">
                                {searchQuery.data?.map((p) => (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setProductId(p.id);
                                                setInternalCode(p.code ?? suggestInternal(row));
                                                setSupplierCode(p.supplier_code ?? suggestSupplier(row));
                                            }}
                                            className="flex w-full items-center gap-2 rounded-md border border-transparent bg-white px-2 py-2 text-left text-sm hover:border-primary/30 hover:bg-primary/5"
                                        >
                                            {p.image_url ? (
                                                <img
                                                    src={p.image_url}
                                                    alt=""
                                                    className="size-8 rounded object-cover"
                                                />
                                            ) : (
                                                <span className="flex size-8 items-center justify-center rounded bg-gray-100">
                                                    <PiCubeBold className="size-4 text-gray-400" />
                                                </span>
                                            )}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-medium text-gray-800">
                                                    {p.name}
                                                </span>
                                                <span className="font-mono text-xs text-gray-500">
                                                    {p.code ?? '—'}
                                                    {p.supplier_code ? ` · ${p.supplier_code}` : ''}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : searchTerm.length >= 2 && !searchQuery.isFetching ? (
                            <p className="text-xs text-amber-800">Nenhum produto encontrado.</p>
                        ) : null}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setProductId(null)}
                        className="text-xs font-medium text-primary hover:underline"
                    >
                        Trocar produto vinculado
                    </button>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">
                            SKU interno (PO / Ploomes)
                        </span>
                        {row.po_source_codes.length > 0 ? (
                            <span className="mb-1 block font-mono text-[10px] text-emerald-700">
                                Na PO: {row.po_source_codes.join(', ')}
                            </span>
                        ) : null}
                        <input
                            type="text"
                            value={internalCode}
                            onChange={(e) => setInternalCode(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                            placeholder="Ex: OK5003-1"
                        />
                    </label>
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold text-gray-600">
                            SKU fornecedor (PI / China)
                        </span>
                        {row.pi_source_codes.length > 0 ? (
                            <span className="mb-1 block font-mono text-[10px] text-blue-700">
                                Na PI: {row.pi_source_codes.join(', ')}
                            </span>
                        ) : null}
                        <input
                            type="text"
                            value={supplierCode}
                            onChange={(e) => setSupplierCode(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                            placeholder="Código na planilha"
                        />
                    </label>
                </div>
            </div>
        </V2Modal>
    );
}
